import React, { useEffect, useMemo, useState } from "react";
import { FaDownload, FaSync } from "react-icons/fa";
import { SCHREIBEN_API, api, blobDownload } from "./schreibenUtils";

/* Welle 122 (Meeting 08.10.2026, FBG → FVV): Empfänger aus den Umsätzen eines Kontos — alle, die MEHRFACH gezahlt
   oder Geld bekommen haben, geprüft gegen Adressen / Verträge / Mietverträge; dazu Vertragspartner ohne Umsatz.
   Ausgewählt wird wie in den anderen Quellen (umschalten); „alle …" nimmt die sichtbaren Zeilen auf einmal. */
const STATUS = { adresse: ["Adresse bekannt", "#16a34a"], vertrag: ["Vertrag vorhanden", "#2563eb"],
                 fehlt: ["Anschrift fehlt", "#d97706"] };
const euro = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const deDatum = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");
// nur die Empfänger-Felder speichern (empfaenger_json) — Umsatzdetails bleiben in der Liste
const alsEmpfaenger = (e) => ({ quelle: e.quelle, id: e.id, label: e.label, adressblock: e.adressblock, mail: e.mail || "",
  anrede: e.anrede || "", gruppe: e.gruppe, ...(e.extra ? { extra: e.extra } : {}) });

export default function UmsatzEmpfaenger({ absender, empfKey, istGewaehlt, umschalten, setAuswahl, onFehler }) {
  const [konten, setKonten] = useState([]);
  const [konto, setKonto] = useState("");
  const [min, setMin] = useState(2);
  const [seit, setSeit] = useState("2024-01-01");
  const [archiv, setArchiv] = useState(false);
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState("");
  const [richtung, setRichtung] = useState("");
  const [kategorie, setKategorie] = useState("");

  useEffect(() => {
    api(`/umsatz/konten?absender=${encodeURIComponent(absender || "")}`)
      .then((d) => { setKonten(d.konten || []); setKonto((k) => k || (d.konten || [])[0]?.suchbegriff || ""); })
      .catch((e) => onFehler(String(e.message || e)));
  }, [absender, onFehler]);

  const query = () => `konto=${encodeURIComponent(konto)}&min_anzahl=${min}&seit=${encodeURIComponent(seit || "")}`
    + `&archiv=${archiv ? 1 : 0}&absender=${encodeURIComponent(absender || "")}`;
  const laden = async () => {
    if (!konto) return;
    setLaedt(true);
    try { setDaten(await api(`/umsatz/partner?${query()}`)); } catch (e) { onFehler(String(e.message || e)); }
    setLaedt(false);
  };
  useEffect(() => { if (konto && !daten) laden(); }, [konto]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportieren = async () => {
    try {
      const r = await fetch(`${SCHREIBEN_API}/umsatz/export?${query()}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Export fehlgeschlagen");
      await blobDownload(r, `Vertragspartner ${konto}.xlsx`);
    } catch (e) { onFehler(String(e.message || e)); }
  };

  const kategorien = useMemo(() => [...new Set((daten?.eintraege || []).map((e) => e.umsatz?.kategorie).filter(Boolean))].sort(),
    [daten]);
  const passt = (e) => {
    const f = filter.trim().toLowerCase();
    if (status && e.status !== status) return false;
    if (richtung && e.umsatz?.richtung !== richtung) return false;
    if (kategorie && e.umsatz?.kategorie !== kategorie) return false;
    return !f || f.split(/\s+/).every((w) => `${e.label} ${(e.adressblock || []).join(" ")} ${e.umsatz?.name || ""} ${e.vertraege || ""} ${e.umsatz?.kategorie || ""}`
      .toLowerCase().includes(w));
  };
  const sichtbar = (daten?.eintraege || []).filter(passt);
  const ohne = (daten?.ohne_umsatz || []).filter((e) => (!status || e.status === status) && !richtung && !kategorie && passt(e));
  const alleWaehlen = (liste) => setAuswahl((p) => {
    const da = new Set(p.map(empfKey));
    return [...p, ...liste.filter((e) => !da.has(empfKey(e))).map(alsEmpfaenger)];
  });
  const s = daten?.stand;

  const zeile = (e) => {
    const [st, farbe] = STATUS[e.status] || ["", "#94a3b8"];
    const u = e.umsatz;
    return (
      <tr key={empfKey(e)} style={{ cursor: "pointer" }} onClick={() => umschalten(alsEmpfaenger(e))}>
        <td style={{ width: 30 }}><input type="checkbox" readOnly checked={istGewaehlt(e)} /></td>
        <td>
          <b>{e.label}</b>
          <div><span className="ue-badge" style={{ borderColor: farbe, color: farbe }}>{st}</span>
            {e.mail && <span className="ue-klein"> ✉ {e.mail}</span>}</div>
        </td>
        <td className="ue-klein">{(e.adressblock || []).join(", ")}</td>
        <td className="ue-klein" style={{ whiteSpace: "nowrap" }}>
          {u ? <>{u.anzahl}× · {u.richtung}<br />{euro(u.summe)}<br />zuletzt {deDatum(u.zuletzt)}</> : "—"}
        </td>
        <td className="ue-klein">{e.pruefung}{e.vertraege ? <><br />{e.vertraege}</> : null}
          {u?.kategorie ? <><br />Kategorie: {u.kategorie}</> : null}</td>
      </tr>
    );
  };

  return (
    <div className="ue">
      <div className="ue-leiste">
        <label>Konto
          <select className="vg-in" value={konto} onChange={(e) => { setKonto(e.target.value); setDaten(null); }}>
            {konten.map((k) => (
              <option key={k.suchbegriff} value={k.suchbegriff}>
                {k.eigenes ? "★ " : ""}{k.suchbegriff} · {k.inhaber} · {k.anzahl} Buchungen bis {deDatum(k.zuletzt)}
              </option>
            ))}
          </select>
        </label>
        <label>mind. Zahlungen
          <input className="vg-in" type="number" min={1} max={50} value={min} style={{ width: 70 }} onChange={(e) => setMin(Number(e.target.value) || 1)} />
        </label>
        <label>ab
          <input className="vg-in" type="date" value={seit} onChange={(e) => setSeit(e.target.value)} />
        </label>
        <label className="ue-check"><input type="checkbox" checked={archiv} onChange={(e) => setArchiv(e.target.checked)} /> mit Archiv (bis 2022)</label>
        <button className="me-btn me-btn-secondary me-btn-sm" disabled={laedt || !konto} onClick={laden}><FaSync /> {laedt ? "lädt …" : "Laden"}</button>
        <button className="me-btn me-btn-secondary me-btn-sm" disabled={!daten} onClick={exportieren}><FaDownload /> Übersicht als Excel</button>
      </div>
      {s && (
        <div className="ue-klein" style={{ margin: "4px 0 8px" }}>
          {s.buchungen} Buchungen · {s.gesamt} Gegenparteien · <b>{s.mehrfach} mit ≥ {daten.min_anzahl} Zahlungen</b>:{" "}
          <span style={{ color: STATUS.adresse[1] }}>{s.adresse} Adresse bekannt</span> ·{" "}
          <span style={{ color: STATUS.vertrag[1] }}>{s.vertrag} Vertrag vorhanden</span> ·{" "}
          <span style={{ color: STATUS.fehlt[1] }}>{s.fehlt} Anschrift fehlt</span> · {s.ohne_umsatz} Vertragspartner ohne Umsatz
        </div>
      )}
      <div className="ue-leiste">
        <input className="me-filter-input" style={{ flex: "1 1 220px" }} placeholder="Filtern… (Name, Ort, Vertrag, Kategorie)"
               value={filter} onChange={(e) => setFilter(e.target.value)} />
        <select className="vg-in" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">alle Status</option>
          {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="vg-in" value={richtung} onChange={(e) => setRichtung(e.target.value)}>
          <option value="">Ein- und Ausgänge</option>
          <option value="Eingang">nur Eingänge (zahlen an uns)</option>
          <option value="Ausgang">nur Ausgänge (wir zahlen)</option>
          <option value="beides">beides</option>
        </select>
        <select className="vg-in" value={kategorie} onChange={(e) => setKategorie(e.target.value)}>
          <option value="">alle Kategorien</option>
          {kategorien.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>
      <div className="ue-leiste">
        <button className="me-btn me-btn-secondary me-btn-sm" disabled={!sichtbar.length && !ohne.length}
                onClick={() => alleWaehlen([...sichtbar, ...ohne])}>alle sichtbaren wählen ({sichtbar.length + ohne.length})</button>
        <button className="me-btn me-btn-secondary me-btn-sm"
                onClick={() => alleWaehlen([...sichtbar, ...ohne].filter((e) => e.status !== "fehlt"))}>
          nur mit Anschrift wählen ({[...sichtbar, ...ohne].filter((e) => e.status !== "fehlt").length})</button>
      </div>
      <div className="me-table-wrap" style={{ maxHeight: 420, overflow: "auto" }}>
        <table className="me-table">
          <tbody>
            {!daten && <tr><td className="ue-klein">{laedt ? "Umsätze werden ausgewertet …" : "Konto wählen und laden."}</td></tr>}
            {daten && !sichtbar.length && !ohne.length && <tr><td className="ue-klein">Keine Treffer.</td></tr>}
            {sichtbar.map(zeile)}
            {ohne.length > 0 && (
              <tr><td colSpan={5} className="ue-gruppe">Vertragspartner ohne Umsatz auf {daten?.konto?.suchbegriff} (Verträge/Versicherungen der Objekte von {absender})</td></tr>
            )}
            {ohne.map(zeile)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
